import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, formatEuros } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, Sparkles, Users, CalendarOff, CalendarDays, Settings2 } from "lucide-react";
import { toast } from "sonner";

const FEATURES_PAID = [
  { icon: Users, text: "Mecánicos ilimitados (General y Rápida)" },
  { icon: CalendarOff, text: "Vacaciones para cada mecánico" },
  { icon: CalendarDays, text: "Días festivos personalizados" },
  { icon: Sparkles, text: "Citas diarias ilimitadas · Agenda y Rápida" },
];

const FEATURES_FREE = [
  "1 mecánico",
  "Sin vacaciones ni festivos",
  "Máximo 3 citas al día (entre agenda y rápida)",
];

export default function Subscribe() {
  const { workshop, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isLifetime = workshop?.plan === "lifetime";
  const isActive = workshop?.plan === "active";
  const priceCents = workshop?.price_yearly_cents || 4235;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "No se pudo iniciar el pago"));
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/suscripcion`,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(errMsg(e, "No se pudo abrir el portal"));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="subscribe-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Suscripción</h1>
        <p className="text-slate-500 mt-1">Desbloquea todas las funciones para tu taller.</p>
      </header>

      {isLifetime && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="py-6 flex items-center gap-3">
            <Check className="w-6 h-6 text-emerald-700" />
            <div>
              <div className="font-display font-bold text-emerald-900">Acceso vitalicio activado</div>
              <div className="text-sm text-emerald-800">Este taller forma parte de los usuarios fundadores. Todas las funciones están desbloqueadas gratis, para siempre.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {isActive && (
        <Card className="border-blue-300 bg-blue-50">
          <CardContent className="py-6 flex flex-wrap items-center gap-3">
            <Check className="w-6 h-6 text-blue-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-blue-900">Suscripción activa</div>
              <div className="text-sm text-blue-800">
                {workshop?.subscription_current_period_end
                  ? `Renovación: ${new Date(workshop.subscription_current_period_end).toLocaleDateString("es-ES")}`
                  : "Todas las funciones están desbloqueadas."}
              </div>
            </div>
            <Button
              onClick={openPortal}
              disabled={loading}
              variant="outline"
              className="border-blue-300 text-blue-800 hover:bg-blue-100"
              data-testid="manage-billing-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Gestionar suscripción
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="plan-free">
          <CardHeader>
            <CardTitle className="font-display text-2xl">Plan gratuito</CardTitle>
            <div className="text-3xl font-display font-bold text-slate-700">0€</div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-slate-700">
              {FEATURES_FREE.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-4">Se activa automáticamente al finalizar los 3 meses de prueba.</p>
          </CardContent>
        </Card>

        <Card className="border-blue-300 shadow-lg" data-testid="plan-paid">
          <CardHeader>
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">Recomendado</div>
            <CardTitle className="font-display text-2xl">Plan anual</CardTitle>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-display font-bold text-slate-900">{formatEuros(priceCents)}</div>
              <div className="text-sm text-slate-500">/ año</div>
            </div>
            <div className="text-xs text-slate-500">35€ + IVA (21%). Renovación anual automática.</div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-2 text-sm text-slate-800">
              {FEATURES_PAID.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{f.text}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={startCheckout}
              disabled={loading || isLifetime || isActive}
              className="h-12 w-full bg-blue-700 hover:bg-blue-800 text-base"
              data-testid="subscribe-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {loading ? "Redirigiendo..." : isActive ? "Ya estás suscrito" : "Suscribirme ahora"}
            </Button>
            <p className="text-xs text-slate-500 text-center">Pago seguro con Stripe. Tarjeta de prueba: 4242 4242 4242 4242.</p>
          </CardContent>
        </Card>
      </div>

      <div className="text-center">
        <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-home">Volver al inicio</Button>
      </div>
    </div>
  );
}
